import { IconExternalLink, IconLock } from '@tabler/icons-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import styled from 'styled-components'

import { Button } from '../shared-components/BUI/Button'
import { Text } from '../shared-components/BUI/Text'
import { Flexbox } from '../shared-components/Flexbox'

import { NarrowContent } from './CorePageViewEntity'

import { useNavigation } from '@/context/navigationContext'
import { useUser } from '@/context/userContext'
import { useHasRoles } from '@/hooks/useHasRoles'
import { Role } from '@/modules/adminUsers/roles'
import { routes } from '@/utils/index'
import { NavGroup, NavItem } from '@/utils/navigationConfig'

import logo from 'public/buildpass-small.svg'

const IconWrapper = styled.div`
  position: absolute;
  bottom: -12px;
  right: 0px;

  & svg {
    color: ${({ theme }) => theme.colour.neutral.white};
    mix-blend-mode: difference;
  }
`

const LogoWrapper = styled.div`
  position: relative;
`

const LinksWrapper = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colour.secondary._50};
  box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1rem;
`

const getPagesWithAccess = (navigationConfig: NavGroup[]) => {
  const pages: NavItem[] = []

  navigationConfig.forEach((navGroup) => {
    navGroup.items.forEach((navItem) => {
      if (navItem.path) {
        pages.push(navItem)
      }

      navItem.subItems?.forEach((subItem) => {
        if (subItem.path) {
          pages.push(subItem)
        }
      })
    })
  })

  return pages
}

type Props = {
  children?: React.ReactNode
}

export const CorePageNoAccess: React.FC<Props> = ({ children }) => {
  const { builder, userLoading } = useUser()
  const { currentNavItem, navigationConfig } = useNavigation()
  const { push, route } = useRouter()

  const isCustomUser = useHasRoles(Role.Custom)

  if (!isCustomUser || !!currentNavItem || userLoading) return <>{children}</>

  // this probably should be memo-ised but i didn't want to calculate this for normal logged in users, waste of CPU cycles...
  const pagesWithAccess = getPagesWithAccess(navigationConfig)

  if (route === routes.home() && pagesWithAccess?.length) {
    const nextBestPage = pagesWithAccess.find((page) => page.path)
    if (nextBestPage) push(nextBestPage.path)
  }

  // this might break one day when a custom user has access to a page that doesn't appear in the nav bar but hopefully that day is a long long way away
  return (
    <NarrowContent column>
      <Flexbox flexDirection="column" alignItems="center" mt="4rem">
        <Flexbox alignItems="center" gap="0.5rem">
          <LogoWrapper>
            <Image src={logo} alt="BuildPass" width={60} height={60} />
            <IconWrapper>
              <IconLock size={32} />
            </IconWrapper>
          </LogoWrapper>

          <Flexbox flexDirection="column" alignItems="flexStart">
            <Text variant="heading03">No Access</Text>
            <Text variant="label02" secondary="_600">
              You don&apos;t have permission to view this page
            </Text>
          </Flexbox>
        </Flexbox>

        {!!pagesWithAccess?.length && (
          <Flexbox mt="3rem" flexDirection="column" gap="1rem">
            <Text>You can access the following pages</Text>
            <LinksWrapper>
              <Flexbox
                flexWrap="wrap"
                justifyContent="center"
                gap="0.5rem 1rem"
              >
                {pagesWithAccess.map((page) => (
                  <Link href={page.path} key={page.path}>
                    <Button
                      size="small"
                      variant="secondary-text"
                      icon={<IconExternalLink size={16} />}
                      iconPlacement="right"
                    >
                      {page.title}
                    </Button>
                  </Link>
                ))}
              </Flexbox>
            </LinksWrapper>
          </Flexbox>
        )}

        <Text mt="3rem" align="center">
          If you believe you should have access to this page, please contact
          your representative at {builder?.company?.name || 'your organization'}
        </Text>
      </Flexbox>
    </NarrowContent>
  )
}
